/**
 * カタログのスライドショー表示。
 *
 * 大きな表示面と、下部のフィルムストリップ（サムネイルの横並び）で構成する。
 * ストリップをクリックするとその素材までアニメーションで移動し、
 * ←→ キーと左右のボタンでも送れる。表示面のクリックで詳細を開く。
 *
 * 【画質】
 * 一覧用サムネイル（長辺640px）は大画面では粗いので、写真は表示中の
 * 前後1枚ぶんだけ詳細表示用プレビュー（1600px級）を裏で取り、
 * 届き次第差し替える。動画はプロキシ再生が詳細モーダルの仕事なので、
 * ここではサムネイル＋再生マークに留める。
 *
 * 【描画量】
 * スライドは全件をトラックに並べる（並べないと移動アニメーションが作れない）が、
 * 画像を実際に描くのは現在位置の前後2枚まで。数百件でも <img> が増えない。
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Asset } from '../lib/supabase.ts'
import { formatBytes } from '../lib/format.ts'
import { KindIcon } from './AssetThumb.tsx'

export function Slideshow({
  assets,
  thumbs,
  broken,
  placeholder,
  onError,
  onOpen,
  loadPreview,
  keyboardEnabled,
}: {
  assets: Asset[]
  thumbs: Record<string, string>
  broken: Record<string, true>
  placeholder: (a: Asset) => ReactNode
  onError: (assetId: string) => void
  onOpen: (a: Asset) => void
  /** 写真の高解像度プレビューURLを取る（無ければ null）。呼び出し側でキャッシュされる。 */
  loadPreview: (a: Asset) => Promise<string | null>
  /** 詳細モーダルが開いている間は false にして、矢印キーを裏で奪わない。 */
  keyboardEnabled: boolean
}) {
  const [index, setIndex] = useState(0)
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const stripRef = useRef<HTMLDivElement | null>(null)
  // いま見ている素材のID。絞り込みで並びが変わっても同じ素材を追いかける。
  const currentIdRef = useRef<string | null>(null)

  const count = assets.length
  const clamped = Math.min(index, Math.max(0, count - 1))
  const current = assets[clamped]

  const goTo = useCallback(
    (i: number) => {
      if (count === 0) return
      const next = Math.max(0, Math.min(count - 1, i))
      currentIdRef.current = assets[next]?.id ?? null
      setIndex(next)
    },
    [assets, count],
  )

  // 絞り込み・検索で並びが変わったら、見ていた素材の新しい位置へ移す。
  // 消えていたら位置だけ範囲内に収める（先頭へ飛ばすと文脈を失う）。
  useEffect(() => {
    const id = currentIdRef.current
    const found = id ? assets.findIndex((a) => a.id === id) : -1
    setIndex((prev) => {
      const next = found >= 0 ? found : Math.min(prev, Math.max(0, assets.length - 1))
      currentIdRef.current = assets[next]?.id ?? null
      return next
    })
  }, [assets])

  // 表示中の写真（と前後1枚）の高解像度プレビューを取り寄せる
  useEffect(() => {
    let cancelled = false
    for (const a of [assets[clamped], assets[clamped + 1], assets[clamped - 1]]) {
      if (!a || a.kind !== 'image' || previews[a.id]) continue
      void loadPreview(a).then((url) => {
        if (!cancelled && url) setPreviews((prev) => (prev[a.id] ? prev : { ...prev, [a.id]: url }))
      })
    }
    return () => {
      cancelled = true
    }
  }, [assets, clamped, loadPreview, previews])

  // フィルムストリップを現在位置へスクロールして追従させる
  useEffect(() => {
    const el = stripRef.current?.querySelector(`[data-idx="${clamped}"]`)
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [clamped])

  useEffect(() => {
    if (!keyboardEnabled) return
    const onKey = (e: KeyboardEvent) => {
      // 検索欄などの入力中に横取りしない
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return
      // 設定などのダイアログが開いている間は、その中の矢印キーで裏のスライドを動かさない
      if (t?.closest('dialog[open]')) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goTo(clamped - 1)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goTo(clamped + 1)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [keyboardEnabled, goTo, clamped])

  if (count === 0 || !current) {
    return <p className="muted pad">該当する素材がありません。</p>
  }

  return (
    <div className="slideshow">
      <div className="slide-stage">
        <div className="slide-track" style={{ transform: `translateX(-${clamped * 100}%)` }}>
          {assets.map((a, i) => {
            // 画像を描くのは前後2枚まで。それ以外は空の枠だけ置いて位置を保つ。
            if (Math.abs(i - clamped) > 2) return <div className="slide" key={a.id} />
            const src = a.kind === 'image' ? (previews[a.id] ?? thumbs[a.id]) : thumbs[a.id]
            const showImg = src && !broken[a.id]
            return (
              <figure className="slide" key={a.id}>
                <button
                  type="button"
                  className="slide-open"
                  tabIndex={i === clamped ? 0 : -1}
                  onClick={() => onOpen(a)}
                  aria-label={`${a.title} の詳細を開く`}
                >
                  {showImg ? (
                    <img
                      src={src}
                      alt={a.title}
                      draggable={false}
                      onError={() => onError(a.id)}
                    />
                  ) : (
                    <span className="thumb-placeholder">{placeholder(a)}</span>
                  )}
                  {a.kind === 'video' && (
                    <span className="slide-play" aria-hidden="true">
                      <KindIcon kind="video" />
                    </span>
                  )}
                </button>
                <figcaption className="slide-cap">
                  <span className="slide-cap-kind" aria-hidden="true">
                    <KindIcon kind={a.kind} />
                  </span>
                  <span className="slide-cap-title">{a.title}</span>
                  <span className="slide-cap-size">{formatBytes(a.byte_size)}</span>
                </figcaption>
              </figure>
            )
          })}
        </div>

        <span className="slide-counter">
          {clamped + 1} / {count}
        </span>
        <button
          type="button"
          className="slide-nav prev"
          onClick={() => goTo(clamped - 1)}
          disabled={clamped === 0}
          aria-label="前の素材へ"
        >
          ‹
        </button>
        <button
          type="button"
          className="slide-nav next"
          onClick={() => goTo(clamped + 1)}
          disabled={clamped === count - 1}
          aria-label="次の素材へ"
        >
          ›
        </button>
      </div>

      <div className="filmstrip" ref={stripRef} role="listbox" aria-label="素材の一覧">
        {assets.map((a, i) => (
          <button
            type="button"
            key={a.id}
            data-idx={i}
            className={i === clamped ? 'fs-item active' : 'fs-item'}
            role="option"
            aria-selected={i === clamped}
            title={a.title}
            onClick={() => goTo(i)}
          >
            {thumbs[a.id] && !broken[a.id] ? (
              <img src={thumbs[a.id]} alt={a.title} loading="lazy" onError={() => onError(a.id)} />
            ) : (
              <span className="fs-empty" aria-hidden="true" />
            )}
            <span className="fs-kind" aria-hidden="true">
              <KindIcon kind={a.kind} />
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
